import classNames from 'classnames'
import * as React from 'react'
import {useLazyLoadQuery, graphql, useFragment} from 'react-relay'

import BuildLineLayout from '../../../../../containers/BuildLineLayout'
import BuildsFetcher from '../../../../../containers/BuildsFetcher'
import {OneLineDetector} from '../../../../../containers/OneLineDetector'
import {useAppSelector} from '../../../../../hooks/react-redux'
import {useSyncedBooleanQueryParam} from '../../../../../hooks/routes'
import type {State} from '../../../../../reducers/types'
import {getProjectsHash} from '../../../../../selectors'
import {getProjectsData} from '../../../../../selectors/projectTrees'
import {stringifyId} from '../../../../../types'
import {UserProperties} from '../../../../../utils/userProperties'
import {useBuildsClassName} from '../../../../common/Builds/Builds'
import ContentPanel from '../../../../common/ContentPanel/ContentPanel'
import Grid from '../../../../common/Grid/Grid'
import {useSequenceLoaderReadyHandler} from '../../../../common/SequenceLoader/SequenceLoader.hooks'

import type {DeploymentsQuery} from './__generated__/DeploymentsQuery.graphql'
import Deployment from './Deployment.container'
import {deploymentsQuery, getDeploymentsQueryVariables} from './Deployments.queries'
import {getDeploymentsLocator} from './Deployments.rest'
import type {
  DeploymentsProps,
  DeploymentGridProps,
  WrapperDeploymentsProps,
} from './Deployments.types'

import styles from './Deployments.css'

const fragment = graphql`
  fragment DeploymentsFragment on BuildTypes {
    buildType {
      rawId
    }
  }
`

function WrapperDeployments({
  possibleSnapshotDeploymentsKey,
  possibleArtifactDeploymentsKey,
  buildId,
  buildTypeId,
}: WrapperDeploymentsProps) {
  const possibleArtifactDeploymentsBuildType = useFragment(fragment, possibleArtifactDeploymentsKey)
    ?.buildType
  const possibleSnapshotDeploymentsBuildType = useFragment(fragment, possibleSnapshotDeploymentsKey)
    ?.buildType
  const possibleArtifactDeploymentsSet = React.useMemo(
    () => new Set(possibleArtifactDeploymentsBuildType?.map(({rawId}) => rawId)),
    [possibleArtifactDeploymentsBuildType],
  )
  const possibleSnapshotDeploymentsSet = React.useMemo(
    () => new Set(possibleSnapshotDeploymentsBuildType?.map(({rawId}) => rawId)),
    [possibleSnapshotDeploymentsBuildType],
  )
  const projectIds = useAppSelector((state: State) => getProjectsData(state))
  const projects = useAppSelector((state: State) => getProjectsHash(state))
  const order = React.useMemo(
    () =>
      projectIds.flatMap(projectId => {
        const buildTypeIds = projects[projectId]?.buildTypes?.buildType ?? []
        return buildTypeIds.filter(
          id => possibleArtifactDeploymentsSet.has(id) || possibleSnapshotDeploymentsSet.has(id),
        )
      }),
    [possibleArtifactDeploymentsSet, possibleSnapshotDeploymentsSet, projectIds, projects],
  )

  if (!order.length) {
    return null
  }

  return (
    <BuildsFetcher locator={getDeploymentsLocator(buildId)} withQueued>
      <OneLineDetector>
        <BuildLineLayout showPath={false}>
          <DeploymentGrid>
            {order.map(id => (
              <Deployment
                key={stringifyId(id)}
                buildId={buildId}
                buildTypeId={buildTypeId}
                deployTypeId={id}
                artifactOnly={!possibleSnapshotDeploymentsSet.has(id)}
              />
            ))}
          </DeploymentGrid>
        </BuildLineLayout>
      </OneLineDetector>
    </BuildsFetcher>
  )
}

function DeploymentGrid({children}: DeploymentGridProps) {
  return <Grid className={classNames(styles.grid, useBuildsClassName())}>{children}</Grid>
}

function Deployments({buildId, buildTypeId}: DeploymentsProps) {
  const panelType = UserProperties.EXPAND_BUILD_DEPLOYMENTS_SECTION
  const [isExpandedDeploymentsSection] = useSyncedBooleanQueryParam({
    param: panelType,
    userProperty: panelType,
  })
  const isExpandedSectionRef = React.useRef(isExpandedDeploymentsSection)
  const isExpandedSection = isExpandedDeploymentsSection || isExpandedSectionRef.current
  const {possibleSnapshotDeployments, possibleArtifactDeployments} =
    useLazyLoadQuery<DeploymentsQuery>(deploymentsQuery, getDeploymentsQueryVariables(buildTypeId))
  const hasBuildTypes = possibleSnapshotDeployments?.count || possibleArtifactDeployments?.count

  useSequenceLoaderReadyHandler(true)

  React.useEffect(() => {
    if (isExpandedDeploymentsSection) {
      isExpandedSectionRef.current = isExpandedDeploymentsSection
    }
  }, [isExpandedDeploymentsSection])

  if (!hasBuildTypes) {
    return null
  }

  return (
    <ContentPanel
      panelType={panelType}
      heading="Deployments"
      withBorder={false}
      content={
        isExpandedSection ? (
          <WrapperDeployments
            possibleSnapshotDeploymentsKey={possibleSnapshotDeployments}
            possibleArtifactDeploymentsKey={possibleArtifactDeployments}
            buildId={buildId}
            buildTypeId={buildTypeId}
          />
        ) : null
      }
    />
  )
}

export default Deployments
